import {
  useDynamicContext,
  useTokenBalances,
} from "@dynamic-labs/sdk-react-core";
import { AntDesign } from "@expo/vector-icons";
import { router, useFocusEffect } from "expo-router";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { View, Text, Pressable, ScrollView } from "react-native";
import Animated, { FadeInRight, FadeOutLeft } from "react-native-reanimated";
import { useDispatch } from "react-redux";
import { useBalance } from "wagmi";

import CardToken from "@/components/card/CardToken";
import FiatToCryptoDepositModal from "@/components/modal/banking/web3/FiatToCryptoDepositModal";
import WalletInformation from "@/components/sections/web3/WalletInformation";
import Status from "@/components/web3/Status";
import { WalletWidget } from "@/components/web3/WalletWidget";
import { setForm } from "@/context/actions/actions";
import { getIsBridgeActiveFromCookies } from "@/utils/cookies/Cookies";
import { checkIcon } from "@/utils/transactions/ExchangeCurrencies";

export default function Index() {
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [tronWalletAddress, setTronWalletAddress] = useState("");
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { primaryWallet, network, networkConfigurations } = useDynamicContext();
  const { tokenBalances } = useTokenBalances();
  const isBridgeApproved = getIsBridgeActiveFromCookies();

  const { data } = useBalance({
    address: primaryWallet?.address as `0x${string}`,
    chainId: network as number,
  });

  useFocusEffect(
    React.useCallback(() => {
      dispatch(setForm({ currentMenuItem: t("home/home:option.web3") }));
      return () => {
        setShowDepositModal(false);
      };
    }, [])
  );

  const findBlockchainName = (chainId: number): string => {
    const chains = networkConfigurations?.evm;
    const networkConfig = chains.find((network) => network.chainId === chainId);
    return networkConfig?.name ?? "";
  };

  return (
    <>
      {isBridgeApproved !== "ACTIVE" ? (
        <Status status={isBridgeApproved} />
      ) : (
        <ScrollView contentContainerClassName="flex-grow bg-white1 items-center p-4 lg:p-0">
          <View className="w-full items-center gap-1">
            <View className="flex-row items-center justify-center gap-2">
              <Text className="text-2xl text-center">
                {t("home/web3/index:title")}
              </Text>
            </View>
            <Text className="text-lg text-darkergray font-light text-center">
              {t("home/web3/index:subtitle")}
            </Text>
          </View>
          <View className="z-50 w-full items-center mt-4">
            <WalletWidget
              setWalletAddress={(address) => console.log("Connected", address)}
              setWidgetInformation={(widgetInformation) => {
                if (widgetInformation.isTronWallet) {
                  setTronWalletAddress(widgetInformation.walletAddress);
                }
              }}
            />
          </View>
          <View className="flex-row w-full items-center justify-center mt-2">
            {primaryWallet && data && (
              <View className="flex-row items-center gap-2 bg-gray border border-lightgray rounded-lg p-2">
                {checkIcon(data?.symbol, 28)}
                <Text className="text-lg">{data?.formatted}</Text>
                <Text className="text-lg font-light">{data?.symbol}</Text>
              </View>
            )}
            {tokenBalances?.length > 0 && (
              <View className="items-center mx-2 gap-2">
                {tokenBalances
                  .filter(
                    (token) =>
                      token.symbol === "USDC" || token.symbol === "USDT"
                  )
                  .map((token, index) => (
                    <CardToken
                      key={index}
                      symbol={token.symbol}
                      balance={token.balance}
                    />
                  ))}
              </View>
            )}
          </View>
          <Animated.View
            className="w-full flex-grow gap-4 justify-center flex-col lg:flex-row p-6"
            entering={FadeInRight}
            exiting={FadeOutLeft}
          >
            <View className="w-full lg:w-1/2 items-center justify-between border border-gray p-5 shadow rounded-xl">
              <Text className="text-2xl font-light">
                {t("home/web3/index:sections.wallet.title")}
              </Text>
              <View className="w-full items-center">
                <WalletInformation
                  address={
                    tronWalletAddress
                      ? tronWalletAddress
                      : primaryWallet?.address
                  }
                  chain={
                    tronWalletAddress
                      ? "TRON Mainnet"
                      : network
                        ? findBlockchainName(network as number)
                        : ""
                  }
                />
              </View>
              <View className="flex-row gap-2">
                <Pressable
                  onPress={() => router.push("/home/transaction/contacts")}
                  className={`w-24 flex-row items-center rounded-lg p-1 ${
                    primaryWallet || tronWalletAddress
                      ? "bg-purple1 hover:bg-purple3"
                      : "bg-gray"
                  }`}
                  disabled={!primaryWallet && !tronWalletAddress}
                >
                  <View className="w-1/3 items-center">
                    <AntDesign
                      name="arrowup"
                      className="text-white1"
                      size={18}
                    />
                  </View>
                  <View className="pl-0.5 w-2/3 items-start justify-center">
                    <Text className="text-base text-white1">
                      {t("common:terms.send")}
                    </Text>
                  </View>
                </Pressable>
                <Pressable
                  onPress={() => setShowDepositModal(true)}
                  className={`w-24 flex-row items-center rounded-lg p-1 ${
                    primaryWallet || tronWalletAddress
                      ? "bg-purple1 hover:bg-purple3"
                      : "bg-gray"
                  }`}
                  disabled={!primaryWallet && !tronWalletAddress}
                >
                  <View className="w-1/3 items-center">
                    <AntDesign
                      name="arrowdown"
                      className="text-white1"
                      size={18}
                    />
                  </View>
                  <View className="w-2/3 items-start justify-center">
                    <Text className="text-base text-white1">
                      {t("common:terms.deposit")}
                    </Text>
                  </View>
                </Pressable>
              </View>
            </View>
            <View className="w-full lg:w-1/2 items-center border border-gray p-4 shadow rounded-xl">
              <Text className="text-2xl font-light">
                {t("home/web3/index:sections.transactions.title")}
              </Text>
            </View>
          </Animated.View>
          <FiatToCryptoDepositModal
            visible={showDepositModal}
            onClose={() => setShowDepositModal(false)}
            walletAddress={
              tronWalletAddress ? tronWalletAddress : primaryWallet?.address
            }
            rail={
              tronWalletAddress
                ? "TRON Mainnet"
                : network
                  ? findBlockchainName(network as number)
                      .split(" ")[0]
                      .toLowerCase()
                  : ""
            }
          />
        </ScrollView>
      )}
    </>
  );
}
